import React, { useState, useEffect } from "react";
import "./InstagramFeed.css";
import { api, transformInstagramPost } from "../services/api";
import CloudinaryImage from "./CloudinaryImage";
import StickyHeadline from "./StickyHeadline";
import {
  Headline,
  HighlightedQuote,
  Icon,
  Button,
  Section,
  ContentBlock,
} from "./ui";

export function InstagramFeed({
  maxHeight = "420px",
  darkMode = false,
  stickyHeadline = null,
  navbarHeight = 0,
}) {
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktopCarousel, setIsDesktopCarousel] = useState(false);
  const [visibleSlides, setVisibleSlides] = useState(3);

  useEffect(() => {
    const fetchInstagramPosts = async () => {
      try {
        const posts = await api.getInstagramPosts();
        const transformedPosts = posts
          .map(transformInstagramPost)
          .filter((post) => post && post.isVisible);
        setInstagramPosts(transformedPosts.slice(0, 6)); // Show more posts for carousel
      } catch (error) {
        console.error("Error loading Instagram posts:", error);
        // Fallback to original data if API fails
        setInstagramPosts([
          {
            id: 1,
            image: `./gulf_sony/2.jpg`,
            instagramUrl: "https://instagram.com/wtm.sportwagen",
          },
          {
            id: 2,
            image: `./gulf_sony/4.jpg`,
            instagramUrl: "https://instagram.com/wtm.sportwagen",
          },
          {
            id: 3,
            image: `./gulf_sony/10.jpg`,
            instagramUrl: "https://instagram.com/wtm.sportwagen",
          },
          {
            id: 4,
            image: `./gulf_sony/9.jpg`,
            instagramUrl: "https://instagram.com/wtm.sportwagen",
          },
          {
            id: 5,
            image: `./gulf_sony/1.jpg`,
            instagramUrl: "https://instagram.com/wtm.sportwagen",
          },
          {
            id: 6,
            image: `./porsche_rot/DSC05618.jpg`,
            instagramUrl: "https://instagram.com/wtm.sportwagen",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramPosts();
  }, []);

  // Handle screen resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 700;
      setIsDesktopCarousel(isDesktop);

      if (isDesktop) {
        const slideWidth = getSlideWidth();
        const containerWidth = window.innerWidth;
        const calculatedVisibleSlides = Math.floor(containerWidth / slideWidth);
        setVisibleSlides(calculatedVisibleSlides);

        // Reset current index if it's now out of bounds
        setCurrentIndex((prev) => {
          const maxIndex = Math.max(
            0,
            instagramPosts.length - calculatedVisibleSlides
          );
          return Math.min(prev, maxIndex);
        });
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [instagramPosts.length]);

  // Calculate slide width based on screen size
  const getSlideWidth = () => {
    if (window.innerWidth <= 1100) return 400 + 20; // slide width + margin
    if (window.innerWidth <= 1400) return 450 + 20;
    return 550 + 20;
  };

  // Navigation handlers
  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    const maxIndex = Math.max(0, instagramPosts.length - visibleSlides);
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  return (
    <div className="relative">
      {/* Sticky Headline - Full Width */}
      {stickyHeadline && (
        <StickyHeadline headline={stickyHeadline} navbarHeight={navbarHeight} />
      )}

      <Section
        variant={darkMode ? "primary" : "white"}
        spacing="spacious"
        className=" overflow-hidden"
        margin={false}
      >
        <ContentBlock maxWidth="4xl" spacing="comfortable" className="mx-auto">
          <div className="instagram-header text-center  max-w-4xl mx-auto ">
            <div className="flex flex-col sm:flex-row items-center sm:justify-left  mb-4">
              <div className="instagram-icon bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 rounded-md p-3 sm:mb-4 sm:mb-0 sm:mr-4 flex flex-row items-center">
                <svg
                  className="sm:w-12 sm:h-12 w-10 h-10 text-white"
                  fill={
                    darkMode ? "var(--color-primary)" : "var(--color-secondary)"
                  }
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.40z" />
                </svg>
                <a
                  href="https://instagram.com/wtm.sportwagen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform duration-200 hover:scale-[1.02] cursor-pointer inline-block"
                >
                  <Headline
                    variant="h2"
                    color={darkMode ? "primary" : "secondary"}
                    className="sm:text-left text-custom-xl mx-4 sm:ml-8 mt-0.5"
                    noSpacing
                  >
                    WTM.SPORTWAGEN
                  </Headline>
                </a>
              </div>
            </div>
            <div className="mt-8 mb-18 sm:mb-24">
              <HighlightedQuote
                variant="medium"
                borderHeight="tall"
                className="text-left"
                noSpacing
                color={darkMode ? "secondary" : "primary"}
              >
                Unsere Leidenschaft für besondere Sportwagen verbindet – weit
                über Gießen hinaus. Werden Sie Teil unserer Community.
              </HighlightedQuote>
            </div>
          </div>
        </ContentBlock>

        {isDesktopCarousel ? (
          /* Desktop Carousel - Bleeding Edge Layout */
          <div className="relative">
            <div className="instagram-carousel-container">
              <div
                className="instagram-carousel-track"
                style={{
                  transform: `translateX(-${currentIndex * getSlideWidth()}px)`,
                }}
              >
                {instagramPosts.map((post, index) => (
                  <div key={post.id} className="instagram-carousel-slide group">
                    <CloudinaryImage
                      src={post.image}
                      alt={post.altText || "Instagram post"}
                      className="w-full h-full object-cover transition-transform duration-300"
                      style={{ maxHeight: maxHeight }}
                      width={500}
                      height={500}
                      crop="fill"
                      gravity="center"
                      webpFallback={true}
                    />

                    {/* Instagram Icon - Top Right */}
                    <div className="absolute top-3 right-3 bg-white bg-opacity-90 rounded-full p-2 shadow-lg">
                      <svg
                        className="w-4 h-4 text-gray-800"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </div>

                    {/* Hover Overlay with Follow Button */}
                    <div
                      className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-20"
                      style={{ maxHeight: maxHeight }}
                    >
                      <Button
                        onClick={() =>
                          post.instagramUrl &&
                          window.open(
                            post.instagramUrl,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        variant={darkMode ? "primary" : "secondary"}
                        size="medium"
                      >
                        Folgen Sie uns
                        <Icon name="arrow-forward" size="sm" className="ml-2" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Dots */}
            {(() => {
              const totalDots = Math.max(
                0,
                instagramPosts.length - visibleSlides + 1
              );

              return (
                totalDots > 1 && (
                  <div className="flex justify-center mt-6 space-x-2">
                    {Array.from({ length: totalDots }).map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-200 ${
                          index === currentIndex
                            ? darkMode
                              ? "bg-white scale-110"
                              : "bg-gray-800 scale-110"
                            : darkMode
                            ? "bg-gray-400 hover:bg-gray-300"
                            : "bg-gray-300 hover:bg-gray-400"
                        }`}
                      />
                    ))}
                  </div>
                )
              );
            })()}
          </div>
        ) : (
          /* Mobile Grid - 2x2 Layout */
          <>
            <div className="grid grid-cols-2 gap-2 mb-6 px-2">
              {instagramPosts.slice(0, 4).map((post, index) => (
                <div
                  key={post.id}
                  className="relative overflow-hidden rounded-sm aspect-square"
                >
                  <CloudinaryImage
                    src={post.image}
                    alt={post.altText || "Instagram post"}
                    className="w-full h-full max-h-[300px] object-cover"
                    width={400}
                    height={400}
                    crop="fill"
                    gravity="center"
                    webpFallback={true}
                  />

                  {/* Instagram Icon - Top Right */}
                  <div className="absolute top-2 right-2 bg-white bg-opacity-90 rounded-full p-1.5 shadow-lg">
                    <svg
                      className="w-3 h-3 text-gray-800"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            {/* Follow Button under mobile grid */}
            <div className="flex justify-center px-2">
              <Button
                onClick={() =>
                  window.open(
                    "https://instagram.com/wtm.sportwagen",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                variant={darkMode ? "secondary" : "primary"}
                size="medium"
              >
                Folgen Sie uns
                <Icon name="arrow-forward" size="sm" className="ml-2" />
              </Button>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
